import { Component, OnInit, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { CdpCorridorMappingService, CdpCorridorMapping } from '../../services/cdp-corridor-mapping.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { CdpCorridorFilterOptions, CdpPayload } from '../../services/interfaces/cdp.corridor-mapping.interfaces';
import { MatDialog } from '@angular/material/dialog';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-cdp-corridor-mapping',
  standalone: false,
  templateUrl: './cdp-corridor-mapping.component.html',
  styleUrl: './cdp-corridor-mapping.component.scss'
})
export class CdpCorridorMappingComponent implements OnInit {
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;
  spinnerBoolean: boolean = true;

  cdpMappings: CdpCorridorMapping[] = [];

  selectedRows: CdpCorridorMapping[] = [];
  filteredCdpMappings: CdpCorridorMapping[] = [];

  showFloatingFilter: boolean = false;
  activeFilter: string = '';
  dropdownPosition: { top: number; left: number } = { top: 0, left: 0 };
  filterSearch: { [key: string]: string } = {};
  filterColumnsData: CdpCorridorFilterOptions = {
    developmentArea: [],
    facilityType: [],
    cdpsToGroup: [],
    connectedCdpNetworkName: [],
  };
  isLoadingFilterOptions: boolean = false;

  multiFilters: {
    developmentArea: string[];
    facilityType: string[];
    cdpsToGroup: string[];
    connectedCdpNetworkName: string[];
  } = {
    developmentArea: [],
    facilityType: [],
    cdpsToGroup: [],
    connectedCdpNetworkName: [],
  };

  currentFilters: {
    developmentArea: string | null;
    facilityType: string | null;
    cdpsToGroup: string | null;
    connectedCdpNetworkName: string | null;
  } = {
    developmentArea: null,
    facilityType: null,
    cdpsToGroup: null,
    connectedCdpNetworkName: null,
  };

  developmentAreas: string[] = [];
  facilityTypes: string[] = [];
  cdpsByDevArea: { [devArea: string]: string[] } = {};

  constructor(
    private cdpCorridorMappingService: CdpCorridorMappingService,
    private toastr: ToastrService,
    private authService: AuthService,
    private dialog: MatDialog
  ) {
    this.filteredCdpMappings = [...this.cdpMappings];
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.loadDevelopmentAreas();
      await this.loadFacilityTypes();
      await this.loadFilterOptionsInit();
      await this.loadCdpMappings();
    } finally {
      this.spinnerBoolean = false;
    }
  }

  private hasAppliedFilters(): boolean {
    return Object.values(this.currentFilters).some((value) => value !== null);
  }

  private buildFilterRequest(): CdpPayload {
    return {
      developmentArea: this.currentFilters.developmentArea,
      facilityTypes: this.currentFilters.facilityType,
      cdpNames: this.currentFilters.cdpsToGroup,
      pseudoCDPNames: this.currentFilters.connectedCdpNetworkName,
    };
  }

  private setFilterColumnsData(filterOptions: CdpCorridorFilterOptions): void {
    this.filterColumnsData = {
      developmentArea: [...(filterOptions.developmentArea || [])],
      facilityType: [...(filterOptions.facilityType || [])],
      cdpsToGroup: [...(filterOptions.cdpsToGroup || [])],
      connectedCdpNetworkName: [...(filterOptions.connectedCdpNetworkName || [])],
    };
  }

  async loadFilterOptionsInit(): Promise<void> {
    this.isLoadingFilterOptions = true;

    try {
      const filterOptions = await this.cdpCorridorMappingService.getFilterOptionsBySelection({
        developmentArea: null,
        facilityTypes: null,
        cdpNames: null,
        pseudoCDPNames: null,
      });

      this.setFilterColumnsData(filterOptions);
    } catch (error) {
      this.toastr.error('Failed to load filter options', 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async loadDevelopmentAreas(): Promise<void> {
    try {
      this.developmentAreas =
        await this.cdpCorridorMappingService.getDevelopmentAreas();
    } catch (error) {
      this.toastr.error('Failed to load development areas', 'Error');
    }
  }

  async loadFacilityTypes(): Promise<void> {
    try {
      this.facilityTypes =
        await this.cdpCorridorMappingService.getFacilityTypes();
    } catch (error) {
      this.toastr.error('Failed to load facility types', 'Error');
    }
  }

  async loadCdpMappings(): Promise<void> {
    try {
      const mappings = await this.cdpCorridorMappingService.getCdpCorridorMappingsBySelection(
        this.buildFilterRequest()
      );

      if (mappings && mappings.length > 0) {
        this.cdpMappings = mappings.map((mapping) => {
          const isValidDevArea = this.developmentAreas.includes(
            mapping.developmentArea
          );

          if (!isValidDevArea && mapping.developmentArea) {
            this.developmentAreas.push(mapping.developmentArea);
            this.developmentAreas.sort();
          }

          return {
            ...mapping,
            isEditing: true,
            isNew: false,
          };
        });

        const uniqueDevAreas = [
          ...new Set(mappings.map((m) => m.developmentArea).filter(Boolean)),
        ];

        for (const devArea of uniqueDevAreas) {
          await this.loadCdpsForDevArea(devArea);
        }

        this.applyAllFilters();
      } else {
        this.cdpMappings = this.hasAppliedFilters()
          ? []
          : [
              {
                developmentArea: '',
                facilityType: '',
                cdpsToGroup: [],
                connectedCdpNetworkName: '',
                isEditing: true,
                isNew: true,
              },
            ];
        this.applyAllFilters();
        if (!this.hasAppliedFilters()) {
          this.toastr.info('No existing CDP corridor mappings found', 'Info');
        }
      }
    } catch (error) {
      this.toastr.error('Failed to load CDP corridor mappings', 'Error');
    }
  }

  async onDevelopmentAreaChange(mapping: CdpCorridorMapping): Promise<void> {
    this.markAsEdited(mapping);

    mapping.cdpsToGroup = [];
    mapping.connectedCdpNetworkName = '';

    if (mapping.developmentArea) {
      await this.loadCdpsForDevArea(mapping.developmentArea);
    }
  }

  async loadCdpsForDevArea(devArea: string): Promise<void> {
    try {
      const cdps = await this.cdpCorridorMappingService.getCdpsByDevArea(devArea);
      this.cdpsByDevArea[devArea] = cdps;
    } catch (error) {
      this.cdpsByDevArea[devArea] = [];
      this.toastr.error(`Failed to load CDPs for ${devArea}`, 'Error');
    }
  }

  getCdpsForDevArea(devArea: string): string[] {
    return this.cdpsByDevArea[devArea] || [];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (
      !target.closest('.floating-filter-dropdown') &&
      !target.closest('.filter-icon')
    ) {
      this.showFloatingFilter = false;
    }
  }

  openFloatingFilter(event: MouseEvent, field: string): void {
    event.stopPropagation();
    this.activeFilter = field;
    this.showFloatingFilter = true;

    const rect = (event.target as HTMLElement).getBoundingClientRect();

    this.dropdownPosition = {
      top: rect.bottom + 5,
      left: Math.max(10, rect.left - 100),
    };

    const dropdownWidth = 220;
    const viewportWidth = window.innerWidth;

    if (this.dropdownPosition.left + dropdownWidth > viewportWidth) {
      this.dropdownPosition.left = viewportWidth - dropdownWidth - 10;
    }

    const dropdownHeight = 200;
    const viewportHeight = window.innerHeight;

    if (this.dropdownPosition.top + dropdownHeight > viewportHeight) {
      this.dropdownPosition.top = rect.top - dropdownHeight - 5;
    }
  }

  getFilterOptions(field: string): string[] {
    const searchTerm = this.filterSearch[field]?.toLowerCase() || '';
    const filterKey = field as keyof CdpCorridorFilterOptions;
    const options = this.filterColumnsData[filterKey] || [];

    return options
      .filter((option) => option.toLowerCase().includes(searchTerm))
      .sort();
  }

  isChecked(field: string, option: string): boolean {
    return (
      this.multiFilters[field as keyof typeof this.multiFilters]?.includes(
        option
      ) || false
    );
  }

  toggleMultiFilter(field: string, option: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const filterKey = field as keyof typeof this.multiFilters;

    if (!this.multiFilters[filterKey]) {
      this.multiFilters[filterKey] = [];
    }

    if (isChecked) {
      if (!this.multiFilters[filterKey].includes(option)) {
        this.multiFilters[filterKey].push(option);
      }
    } else {
      this.multiFilters[filterKey] = this.multiFilters[filterKey].filter(
        (item) => item !== option
      );
    }
  }

  async applyFilter(field: string): Promise<void> {
    const selectedValues = this.multiFilters[field as keyof typeof this.multiFilters] || [];

    this.currentFilters[field as keyof typeof this.currentFilters] =
      selectedValues.length > 0 ? selectedValues.join(',') : null;

    await this.loadCdpMappings();
    await this.reloadFilterOptions();
    this.applyAllFilters();
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  async clearFilter(field: string): Promise<void> {
    const filterKey = field as keyof typeof this.multiFilters;
    this.multiFilters[filterKey] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field as keyof typeof this.currentFilters] = null;

    await this.loadCdpMappings();
    await this.reloadFilterOptions();
    this.applyAllFilters();
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  isFilterActive(field: keyof typeof this.multiFilters): boolean {
    return this.multiFilters[field].length > 0;
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;

    try {
      const filterOptions = await this.cdpCorridorMappingService.getFilterOptionsBySelection(
        this.buildFilterRequest()
      );

      this.setFilterColumnsData(filterOptions);
    } catch (error) {
      this.toastr.error('Failed to reload filter options', 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private applyAllFilters(): void {
    this.filteredCdpMappings = this.cdpMappings.filter((mapping) => {
    if (
      this.multiFilters.developmentArea.length > 0 &&
      !this.multiFilters.developmentArea.includes(mapping.developmentArea)
    ) {
      return false;
    }

    if (
      this.multiFilters.facilityType.length > 0 &&
      !this.multiFilters.facilityType.includes(mapping.facilityType)
    ) {
      return false;
    }

    if (
      this.multiFilters.cdpsToGroup.length > 0 &&
      !this.multiFilters.cdpsToGroup.some((filter) => mapping.cdpsToGroup.includes(filter))
    ) {
      return false;
    }

    if (
      this.multiFilters.connectedCdpNetworkName.length > 0 &&
      !this.multiFilters.connectedCdpNetworkName.includes(mapping.connectedCdpNetworkName)
    ) {
      return false;
    }

    return true;
    });
  }

  addNewRow(): void {
    const newMapping: CdpCorridorMapping = {
      developmentArea: '',
      facilityType: '',
      cdpsToGroup: [],
      connectedCdpNetworkName: '',
      isEditing: true,
      isNew: true,
    };

    this.cdpMappings.unshift(newMapping);
    this.applyAllFilters();

    this.loadDevelopmentAreas();
  }

  async saveRows(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    const changedRows = this.cdpMappings.filter(
      (mapping) => mapping.isNew || this.rowHasChanged(mapping)
    );

    if (changedRows.length === 0) {
      this.toastr.info('No changes to save.');
      return;
    }

    for (const mapping of changedRows) {
      const validation = this.validateMapping(mapping);
      if (!validation.isValid) {
        this.toastr.error(validation.message, 'Validation Error');
        return;
      }
    }

    for (const mapping of changedRows) {
      let saveResponse: any;

      if (mapping.isNew) {
        saveResponse = await this.cdpCorridorMappingService.saveCdpCorridorMapping(
          mapping,
          'INSERT',
          userName
        );
      } else {
        const oldConnectedCdpNetworkName =
          mapping.originalData?.connectedCdpNetworkName ??
          mapping.connectedCdpNetworkName;
        saveResponse = await this.cdpCorridorMappingService.saveCdpCorridorMapping(
          mapping,
          'UPDATE',
          userName,
          oldConnectedCdpNetworkName
        );
      }

      const parsed = ErrorHandler.parseApiResult(saveResponse);

      if (parsed.success) {
        mapping.isNew = false;
        mapping.isEditing = false;
        mapping.originalData = {
          ...mapping,
          cdpsToGroup: [...mapping.cdpsToGroup],
        };
        successCount++;
      } else {
        failureCount++;
        errorMessages.push(parsed.message || 'Unknown error occurred');
      }
    }

    await this.loadDevelopmentAreas();
    await this.loadCdpMappings();

    if (successCount > 0) {
      this.toastr.success(
        `${successCount} CDP corridor mapping(s) saved successfully`,
        'Success'
      );
    }

    if (failureCount > 0) {
      const detailedErrorMsg =
        errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} CDP corridor mapping(s) failed to save`;

      this.toastr.error(detailedErrorMsg, 'Save Error', {
        timeOut: 8000,
        enableHtml: true,
      });
    }
  }

  validateMapping(mapping: CdpCorridorMapping): { isValid: boolean; message: string } {
    if (!mapping.developmentArea) {
      return { isValid: false, message: 'Development Area is required' };
    }

    if (!mapping.facilityType) {
      return { isValid: false, message: 'Facility Type is required' };
    }
    
    if (!mapping.cdpsToGroup || mapping.cdpsToGroup.length === 0) {
      return { isValid: false, message: 'At least 1 CDP must be selected' };
    }
    
    if (!mapping.connectedCdpNetworkName) {
      return { isValid: false, message: 'Connected CDP Network Name is required' };
    }

    return { isValid: true, message: '' };
  }

  cancelEdit(): void {
    const changedRows = this.cdpMappings.filter(
      (mapping) => mapping.isNew || mapping.isEditing
    );

    changedRows.forEach((mapping) => {
      if (mapping.originalData) {
        Object.assign(mapping, mapping.originalData);
        delete mapping.originalData;
        mapping.isEditing = false;
      } else if (mapping.isNew) {
        const mappingIndex = this.cdpMappings.indexOf(mapping);
        if (mappingIndex > -1) {
          this.cdpMappings.splice(mappingIndex, 1);
        }
      }
    });

    this.applyAllFilters();
  }

  clearSelection(): void {
    if (!this.selectedRows.length) {
      return;
    }
    this.selectedRows = [];
    this.toastr.info('Selection cleared.', 'Info');
  }

  canClearSelection(): boolean {
    return this.selectedRows.length > 0;
  }

  async deleteSelectedMappings(): Promise<void> {
    const userName = this.authService.getUserName();

    const names = Array.from(
      new Set(
        this.selectedRows
          .map((r) => (r.connectedCdpNetworkName || '').trim())
          .filter((n) => !!n)
      )
    );

    if (names.length === 0) {
      this.toastr.info(
        'Select at least one CDP corridor mapping with a connected CDP network name to delete.',
        'Info'
      );
      return;
    }

    const confirmMsg =
      names.length === 1
        ? `Delete CDP corridor mapping "${names[0]}"?`
        : `Delete ${names.length} CDP corridor mapping(s)?\n${names.join(', ')}`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    let successCount = 0;
    let failureCount = 0;
    const deleteErrorMessages: string[] = [];

    for (const name of names) {
      const response = await this.cdpCorridorMappingService.deleteCdpCorridorMapping(name, userName);
      const parsed = ErrorHandler.parseApiResult(response);

      if (parsed.success) {
        successCount++;
      } else {
        failureCount++;
        deleteErrorMessages.push(
          parsed.message || `Failed to delete CDP corridor mapping "${name}".`
        );
      }
    }

    if (successCount > 0) {
      this.cdpMappings = this.cdpMappings.filter(
        (r) => !names.includes((r.connectedCdpNetworkName || '').trim())
      );
      this.selectedRows = this.selectedRows.filter(
        (r) => !names.includes((r.connectedCdpNetworkName || '').trim())
      );
    }

    if (this.cdpMappings.length === 0) {
      this.cdpMappings.push({
        developmentArea: '',
        facilityType: '',
        cdpsToGroup: [],
        connectedCdpNetworkName: '',
        isEditing: true,
        isNew: true,
      });
    }

    await this.loadCdpMappings();

    if (successCount) {
      this.toastr.success(
        `${successCount} CDP corridor mapping${
          successCount > 1 ? 's' : ''
        } deleted.`,
        'Deleted'
      );
    }
    if (failureCount) {
      this.toastr.error(deleteErrorMessages.join('<br>'), 'Delete Error', {
        timeOut: 8000,
        enableHtml: true,
      });
    }
  }

  toggleRowSelection(row: CdpCorridorMapping, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      if (!this.selectedRows.includes(row)) {
        this.selectedRows.push(row);
      }
    } else {
      this.selectedRows = this.selectedRows.filter((r) => r !== row);
    }
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {
      developmentArea: [],
      facilityType: [],
      cdpsToGroup: [],
      connectedCdpNetworkName: [],
    };
    this.currentFilters = {
      developmentArea: null,
      facilityType: null,
      cdpsToGroup: null,
      connectedCdpNetworkName: null,
    };
    this.filterSearch = {};

    await this.loadCdpMappings();
    await this.reloadFilterOptions();
    this.applyAllFilters();
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  hasActiveFilters(): boolean {
    return (
      Object.values(this.multiFilters).some((arr) => arr.length > 0) ||
      Object.values(this.filterSearch).some((val) => val && val.length > 0)
    );
  }

  hasEditingRows(): boolean {
    return this.cdpMappings.some((mapping) => mapping.isEditing);
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async exportToCSV(): Promise<void> {
    const exportData = this.filteredCdpMappings.length ? this.filteredCdpMappings : this.cdpMappings;

    if (!exportData.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const headers = ['Development Area', 'Facility Type', 'CDPs to Group', 'Combined CDP Facility Name'];
    const worksheetData = exportData.map((mapping) => ({
      'Development Area': mapping.developmentArea ?? '',
      'Facility Type': mapping.facilityType ?? '',
      'CDPs to Group': Array.isArray(mapping.cdpsToGroup) ? mapping.cdpsToGroup.join('; ') : '',
      'Combined CDP Facility Name': mapping.connectedCdpNetworkName ?? '',
    }));

    await exportRowsToExcel(
      worksheetData,
      headers,
      'cdp-corridor-mapping.xlsx',
      'CDPCorridorMapping'
    );
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.cdpCorridorMappingService.exportAllCdpCorridorMappings();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  addCdp(mapping: CdpCorridorMapping, event: Event): void {
    const selected = (event.target as HTMLSelectElement).value;
    if (selected && !mapping.cdpsToGroup.includes(selected)) {
      this.markAsEdited(mapping);
      mapping.cdpsToGroup.push(selected);
    }
    (event.target as HTMLSelectElement).value = '';
  }

  removeCdp(mapping: CdpCorridorMapping, cdp: string): void {
    this.markAsEdited(mapping);
    mapping.cdpsToGroup = mapping.cdpsToGroup.filter(
      (c) => c !== cdp
    );
  }

  rowHasChanged(mapping: CdpCorridorMapping): boolean {
    if (!mapping.originalData) return false;
    return (
      mapping.developmentArea !== mapping.originalData.developmentArea ||
      mapping.facilityType !== mapping.originalData.facilityType ||
      mapping.connectedCdpNetworkName !== mapping.originalData.connectedCdpNetworkName ||
      mapping.cdpsToGroup.join(',') !==
        mapping.originalData.cdpsToGroup.join(',')
    );
  }

  markAsEdited(mapping: CdpCorridorMapping): void {
    if (!mapping.originalData) {
      mapping.originalData = {
        ...mapping,
        cdpsToGroup: [...mapping.cdpsToGroup],
      };
    }
    mapping.isEditing = true;
  }
}
